package floris.graphics.landing.pages.projects

import floris.graphics.core.utils.MVI
import com.materialdesignsystem.constants.Constants.HEADER_HEIGHT
import com.materialdesignsystem.constants.ListenerTypes
import floris.graphics.repo.ProjectRepo
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.jetbrains.compose.web.css.px

class ProjectViewModel(
    private val projectId: String? = null,
    private val projectRepo: ProjectRepo = ProjectRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) : MVI<ProjectContract.State, ProjectContract.Intent, ProjectContract.Effect> {

    private val _state = MutableStateFlow(ProjectContract.State())
    override val state get() = _state.asStateFlow()

    private val _effect = MutableSharedFlow<ProjectContract.Effect>()
    override val effect = _effect.asSharedFlow()

    init {
        sendIntent(ProjectContract.Intent.LoadProjectDetails)

        // TODO [TicketNumber]: Should this be eventListener be removed as well at a certain point?
        window.addEventListener(
            type = ListenerTypes.Scroll,
            callback = {
                val docElement = document.documentElement ?: return@addEventListener

                val scrollTop = docElement.scrollTop
                val clientHeight = docElement.clientHeight
                val scrollHeight = docElement.scrollHeight

                val percentage = scrollTop / clientHeight

                val scrolledEnoughToShowProjectNavigators = docElement.clientHeight / 4
                val scrolledTillBottomNavigatorIsVisible = (scrollHeight - scrollTop) > clientHeight + 200

                val headerMarginTop = ((HEADER_HEIGHT * 2) * percentage).toInt().px
                val showProjectNavigators = scrollTop > scrolledEnoughToShowProjectNavigators && scrolledTillBottomNavigatorIsVisible

                updateState {
                    it.copy(
                        headerMarginTop = headerMarginTop,
                        showProjectNavigators = showProjectNavigators
                    )
                }
            }
        )
    }

    override fun sendIntent(intent: ProjectContract.Intent) = coroutineScope.launch {
        when (intent) {
            is ProjectContract.Intent.LoadProjectDetails -> loadProjectDetails()
            is ProjectContract.Intent.Navigate -> emitEffect(ProjectContract.Effect.NavigateToNextScreen(intent.route))
        }
    }

    private suspend fun loadProjectDetails() = withContext(Dispatchers.Default) {
        projectId ?: run {
            emitEffect(ProjectContract.Effect.ShowError("Project ID is null"))
            return@withContext
        }

        val projectDetailDeferred = async { projectRepo.getProject(projectId) }
        val nextProjectDeferred = async { projectRepo.getNextProjectId(projectId) }
        val previousProjectDeferred = async { projectRepo.getPreviousProjectId(projectId) }

        val projectDetail = projectDetailDeferred.await()
        val nextProjectId = nextProjectDeferred.await()
        val previousProjectId = previousProjectDeferred.await()

        updateState {
            it.copy(
                project = projectDetail,
                nextProjectId = nextProjectId,
                previousProjectId = previousProjectId
            )
        }
    }

    override fun emitEffect(effect: ProjectContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (ProjectContract.State) -> ProjectContract.State) {
        _state.update(block)
    }
}
